import React, { useState } from 'react';
import Toast from '../components/ui/Toast';
import { requestPasswordRecovery } from '../api/endpoints';
import { navigateTo } from '../utils/navigation';
import { EMAIL_HTML_PATTERN, EMAIL_RE } from '../utils/regex';

export default function ForgotPasswordPage(): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState(false);

  const [toast, setToast] = useState<{
    open: boolean;
    variant: 'success' | 'error' | 'info';
    title: string;
    description?: string;
  }>({ open: false, variant: 'info', title: '' });

  const emailTrimmed = email.trim();
  const emailValid = EMAIL_RE.test(emailTrimmed);
  const showEmailErr = touched && !emailValid && emailTrimmed.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!emailValid) return;

    try {
      await requestPasswordRecovery(emailTrimmed);
      setSubmitted(true);
      setToast({
        open: true,
        variant: 'success',
        title: 'Check your inbox',
        description: "If that email is registered, you'll receive a link to reset your password.",
      });
    } catch {
      // Still transition to submitted (don't leak which emails exist)
      setSubmitted(true);
      setToast({
        open: true,
        variant: 'info',
        title: 'Email sent (if the address exists)',
        description: 'Please check your inbox and spam folder.',
      });
    }
  };

  const shell = (children: React.ReactNode) => (
    <div
      className="relative min-h-dvh flex items-center justify-center p-4"
      style={{ background: 'var(--bg)' }}
    >
      {/* Brand atmospheric — orb, bottom-right */}
      <div
        className="orb"
        aria-hidden
        style={{
          position: 'absolute',
          bottom: '-80px',
          right: '-80px',
          width: 'clamp(240px, 30vw, 440px)',
          opacity: 0.55,
          pointerEvents: 'none',
        }}
      />

      <div style={{ width: '100%', maxWidth: '400px', position: 'relative', zIndex: 1 }}>
        {children}
      </div>

      <Toast
        open={toast.open}
        variant={toast.variant}
        title={toast.title}
        description={toast.description}
        onOpenChange={(open) => { setToast((t) => ({ ...t, open })); }}
        autoCloseMs={5000}
        placement="center"
      />
    </div>
  );

  if (submitted) {
    return shell(
      <div className="card stack-5" style={{ textAlign: 'center' }}>
        <div className="stack-2">
          <h1 className="t-h3">Check your email</h1>
          <p className="t-small t-mute" style={{ margin: 0 }}>
            If an account exists for{' '}
            <span style={{ fontWeight: 500, color: 'var(--ink)' }}>{emailTrimmed}</span>,
            you&apos;ll receive a reset link shortly. It may take a minute or two — check spam too.
          </p>
        </div>

        <div className="stack-3">
          <button className="btn btn--primary btn--full" onClick={() => { navigateTo('/login'); }}>
            Back to log in
          </button>
          <p className="t-small t-mute" style={{ margin: 0 }}>
            Wrong address?{' '}
            <button
              onClick={() => {
                setSubmitted(false);
                setToast((t) => ({ ...t, open: false }));
              }}
              className="t-deep"
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
              }}
            >
              Try another email
            </button>
          </p>
        </div>
      </div>,
    );
  }

  return shell(
    <div className="card stack-5">
      {/* Back link */}
      <button
        type="button"
        onClick={() => { navigateTo('/login'); }}
        aria-label="Back to log in"
        className="row-3 t-small link--subtle"
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
      >
        <svg
          viewBox="0 0 24 24"
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M15 18l-6-6 6-6" />
        </svg>
        <span>Back to log in</span>
      </button>

      {/* Header */}
      <div className="stack-2">
        <h1 className="t-h3">Reset your password</h1>
        <p className="t-small t-mute" style={{ margin: 0 }}>
          Enter the email linked to your account. We&apos;ll send a link to set a new password.
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="stack-3" noValidate>
        <input
          type="email"
          placeholder="Email address"
          value={email}
          pattern={EMAIL_HTML_PATTERN}
          inputMode="email"
          autoComplete="email"
          onChange={(e) => { setEmail(e.target.value); }}
          onBlur={() => { setTouched(true); }}
          className={showEmailErr ? 'field field--error' : 'field'}
          required
        />
        {showEmailErr && (
          <div className="notice notice--error" role="alert">
            Please enter a valid email address.
          </div>
        )}
        <button type="submit" className="btn btn--primary btn--full" disabled={!emailValid}>
          Send reset link
        </button>
      </form>

      {/* Footer copy */}
      <div className="stack-3 t-small t-mute">
        <p style={{ margin: 0 }}>
          No email yet? It may take a minute or two — check spam and promotions too.
        </p>
        <p style={{ margin: 0 }}>
          Know your password?{' '}
          <button
            onClick={() => { navigateTo('/login'); }}
            className="t-deep"
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              textDecoration: 'underline',
              textUnderlineOffset: '2px',
            }}
          >
            Log in
          </button>
        </p>
      </div>
    </div>,
  );
}
